import React, { useRef } from 'react';
import { Box, TextField, Typography, Divider, Paper, Alert } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DescriptionIcon from '@mui/icons-material/Description';
import DeleteIcon from '@mui/icons-material/Delete';
import IconButton from '@mui/material/IconButton';
import {
  FormProps,
  FormHeader,
  AudioRecordingSection,
  WritingStyleSelector,
  InfoTip,
} from './shared';

const AdaptarModeloForm: React.FC<FormProps> = ({
  documentType,
  values,
  onChange,
  uploadedFiles,
  onFileUpload,
  onFileRemove,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedStyle, setSelectedStyle] = React.useState(values.estilo_escrita || 'padrao');

  const handleFieldChange = (fieldId: string, value: any) => {
    onChange({
      ...values,
      [fieldId]: value,
    });
  };

  const handleRecordingComplete = (audioBlob: Blob) => {
    const audioFile = new File([audioBlob], `audio_${Date.now()}.webm`, {
      type: 'audio/webm',
    });
    onFileUpload([audioFile]);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      // Verificar se é DOCX
      if (!file.name.toLowerCase().endsWith('.docx')) {
        alert('Por favor, selecione um arquivo .DOCX');
        return;
      }
      // Verificar tamanho (15MB)
      if (file.size > 15 * 1024 * 1024) {
        alert('O arquivo deve ter no máximo 15MB');
        return;
      }
      onFileUpload([file]);
    }
    // Limpar input para permitir selecionar o mesmo arquivo novamente
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const docxFile = uploadedFiles.find(f => f.name.toLowerCase().endsWith('.docx'));

  return (
    <Box>
      <FormHeader 
        documentType={documentType}
        customTitle="Adaptar Modelo para Novo Caso"
        customDescription="Envie um documento modelo e adapte-o para um novo caso mantendo a estrutura original"
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* 1. Upload do Modelo */}
        <Box>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Faça o envio do arquivo que você deseja adaptar ao novo caso <span style={{ color: '#d32f2f' }}>*</span>
          </Typography>
          
          <input
            ref={fileInputRef}
            type="file"
            accept=".docx"
            style={{ display: 'none' }}
            onChange={handleFileSelect}
          />
          
          {!docxFile ? (
            <Paper
              variant="outlined"
              sx={{
                p: 4,
                textAlign: 'center',
                cursor: 'pointer',
                borderStyle: 'dashed',
                borderColor: 'grey.400',
                bgcolor: 'grey.50',
                '&:hover': { bgcolor: 'grey.100', borderColor: 'primary.main' },
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              <UploadFileIcon sx={{ fontSize: 48, color: 'grey.500', mb: 1 }} />
              <Typography variant="body1" color="text.secondary">
                Arraste e solte seu documento aqui ou <strong>clique aqui</strong> para enviar o documento.
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                (Selecione um .DOCX de até 15mb)
              </Typography>
            </Paper>
          ) : (
            <Paper variant="outlined" sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <DescriptionIcon sx={{ color: 'primary.main', fontSize: 32 }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="body1" fontWeight={500}>{docxFile.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {(docxFile.size / 1024).toFixed(1)} KB
                </Typography>
              </Box>
              <IconButton color="error" onClick={() => {
                const index = uploadedFiles.indexOf(docxFile);
                if (index >= 0) onFileRemove(index);
              }}>
                <DeleteIcon />
              </IconButton>
            </Paper>
          )}
        </Box>

        <Divider />

        {/* Dados do Modelo */}
        <Typography variant="h6" fontWeight={600}>
          Dados do Modelo
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: -2 }}>
          Complete os campos abaixo para personalizar o documento conforme o seu caso.
        </Typography>

        {/* 2. Nome do Autor */}
        <Box>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Nome do Autor <Typography component="span" variant="body2" color="text.secondary">(Opcional)</Typography>
          </Typography>
          <TextField
            fullWidth
            placeholder="Nome do Autor"
            value={values.nome_cliente || ''}
            onChange={(e) => handleFieldChange('nome_cliente', e.target.value)}
            inputProps={{ maxLength: 300 }}
            helperText="Separe os nomes com ';' caso você represente mais de uma parte. Limite de caracteres: 300"
          />
        </Box>

        {/* 3. Nome do Réu */}
        <Box>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Nome do Réu <Typography component="span" variant="body2" color="text.secondary">(Opcional)</Typography>
          </Typography>
          <TextField
            fullWidth
            placeholder="Nome do Réu"
            value={values.parte_contraria || ''}
            onChange={(e) => handleFieldChange('parte_contraria', e.target.value)}
            inputProps={{ maxLength: 300 }}
            helperText="Separe os nomes com ';' caso você represente mais de uma parte. Limite de caracteres: 300"
          />
        </Box>

        {/* 4. Número do Processo */}
        <Box>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Número do Processo <Typography component="span" variant="body2" color="text.secondary">(Opcional)</Typography>
          </Typography>
          <TextField
            fullWidth
            placeholder="Número do Processo"
            value={values.numero_processo || ''}
            onChange={(e) => handleFieldChange('numero_processo', e.target.value)}
            inputProps={{ maxLength: 300 }}
            helperText="Limite de caracteres: 300"
          />
        </Box>

        {/* 5. Valor da Causa */}
        <Box>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Valor da Causa <Typography component="span" variant="body2" color="text.secondary">(Opcional)</Typography>
          </Typography>
          <TextField
            fullWidth
            placeholder="Valor da Causa"
            value={values.valor_causa || ''}
            onChange={(e) => handleFieldChange('valor_causa', e.target.value)}
            inputProps={{ maxLength: 300 }}
            helperText="Limite de caracteres: 300"
          />
        </Box>

        {/* 6. Fatos */}
        <Box>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Fatos <span style={{ color: '#d32f2f' }}>*</span>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={6}
            placeholder="Ex: O autor, Igor Pereira Severino, atualmente com 6 anos, é filho do Requerido Nathan Lucca Severino, fruto da relação com a genitora Bianca Aparecida Pereira. Os genitores estão separados desde abril de 2022, data em que Autor possuía 5 anos."
            value={values.fatos_caso || ''}
            onChange={(e) => handleFieldChange('fatos_caso', e.target.value)}
            inputProps={{ maxLength: 4500 }}
            required
          />
          <Typography
            variant="caption"
            color={(values.fatos_caso || '').length > 0 ? 'success.main' : 'text.secondary'}
            sx={{ mt: 0.5, display: 'block' }}
          >
            {(values.fatos_caso || '').length} de 4500 caracteres permitidos.
          </Typography>
        </Box>

        {/* Informativo sobre o que a IA fará */}
        <Alert severity="info" sx={{ mt: 1 }}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            O que a IA irá fazer com os dados acima:
          </Typography>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            <li>As informações acima irão substituir os fatos do modelo original</li>
            <li>A estrutura do modelo será mantida</li>
            <li>Se houver jurisprudências e argumentos, eles serão preservados</li>
          </ul>
        </Alert>

        {/* Gravação de Áudio */}
        <AudioRecordingSection
          onRecordingComplete={handleRecordingComplete}
          description="Grave um áudio descrevendo os fatos do novo caso que a IA irá transcrever automaticamente."
        />
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Estilo de Escrita */}
      <WritingStyleSelector
        selectedStyle={selectedStyle}
        onStyleChange={(styleId) => {
          setSelectedStyle(styleId);
          handleFieldChange('estilo_escrita', styleId);
        }}
      />

      <InfoTip message="O modelo enviado será analisado pela IA, que manterá a estrutura, argumentos jurídicos e jurisprudências, substituindo apenas os fatos e dados das partes pelos novos informados. Certifique-se de enviar um arquivo DOCX bem estruturado para melhores resultados." />
    </Box>
  );
};

export default AdaptarModeloForm;

